import { useState, useRef, useCallback } from 'react';
import { Info } from 'lucide-react';
import { useConfig } from '../../context/ConfigContext';

// Fallback colors if category doesn't have a color
const FALLBACK_COLORS = [
  '#10b981', '#3b82f6', '#f59e0b', '#ef4444', '#8b5cf6',
  '#ec4899', '#06b6d4', '#84cc16', '#f97316', '#6366f1'
];

export default function CategoryProgressBars({ data, type, title, subtitle, compact = false, onCategoryClick, categories = [] }) {
  const { formatAmount, config } = useConfig();
  const [showTooltip, setShowTooltip] = useState(false);
  const tooltipTimeout = useRef(null);

  const toggleTooltip = useCallback(() => {
    if (tooltipTimeout.current) clearTimeout(tooltipTimeout.current);
    if (showTooltip) {
      setShowTooltip(false);
    } else {
      setShowTooltip(true);
      tooltipTimeout.current = setTimeout(() => setShowTooltip(false), 3000);
    }
  }, [showTooltip]);

  // Convert object format { category: { total, totalTDS, count, color } } to array format
  // For income with TDS, show net amount (total - TDS). Otherwise show gross.
  const dataArray = data && typeof data === 'object' && !Array.isArray(data)
    ? Object.entries(data).map(([category, values], index) => {
        const hasTDS = values.totalTDS && values.totalTDS > 0;
        return {
          category,
          total: type === 'income' && hasTDS ? values.total - values.totalTDS : values.total,
          grossTotal: values.total,
          totalTDS: values.totalTDS || 0,
          count: values.count,
          color: values.color || FALLBACK_COLORS[index % FALLBACK_COLORS.length]
        };
      })
    : Array.isArray(data) ? data : [];

  // Sort by total (descending)
  const sortedData = [...dataArray].sort((a, b) => b.total - a.total);

  // Calculate total
  const total = sortedData.reduce((sum, item) => sum + item.total, 0);

  const titleWithInfo = (
    <div className="flex items-center gap-1.5">
      <span>{title}</span>
      {subtitle && (
        <div className="relative flex items-center">
          <button
            onClick={(e) => { e.stopPropagation(); toggleTooltip(); }}
            className="flex items-center text-surface-300 hover:text-surface-500 transition-colors"
          >
            <Info className={compact ? "w-3 h-3" : "w-3.5 h-3.5"} />
          </button>
          {showTooltip && (
            <div className="absolute left-1/2 -translate-x-1/2 top-full mt-1 px-2.5 py-1.5 bg-surface-800 text-white text-[10px] rounded-lg whitespace-nowrap z-50 shadow-lg">
              {subtitle}
              <div className="absolute -top-1 left-1/2 -translate-x-1/2 w-2 h-2 bg-surface-800 rotate-45" />
            </div>
          )}
        </div>
      )}
    </div>
  );

  if (!sortedData || sortedData.length === 0) {
    return (
      <div className={`rounded-2xl ${compact ? 'p-3' : 'p-6'} bg-white border border-surface-200`}>
        <h3 className={`font-semibold text-surface-900 ${compact ? 'text-xs mb-2' : 'text-lg mb-4'}`}>{titleWithInfo}</h3>
        <div className={`flex items-center justify-center text-surface-400 ${compact ? 'h-24 text-xs' : 'h-32'}`}>
          No data
        </div>
      </div>
    );
  }

  // For compact mode, show only top 4 categories
  const displayData = compact ? sortedData.slice(0, 4) : sortedData;
  const hasMore = compact && sortedData.length > 4;

  return (
    <div className={`rounded-2xl ${compact ? 'p-3' : 'p-6'} bg-white border border-surface-200`}>
      <div className={`flex items-center justify-between ${compact ? 'mb-2' : 'mb-4'}`}>
        <h3 className={`font-semibold text-surface-900 ${compact ? 'text-xs' : 'text-base'}`}>{titleWithInfo}</h3>
        <span
          className={`font-bold ${compact ? 'text-xs' : 'text-base'}`}
          style={{ color: type === 'income' ? config.incomeColor : config.expenseColor }}
        >
          {formatAmount(total)}
        </span>
      </div>

      <div className={`space-y-${compact ? '2' : '3'}`}>
        {displayData.map((item, index) => {
          const percentage = total > 0 ? ((item.total / total) * 100) : 0;
          const isClickable = !!onCategoryClick;

          const content = (
            <>
              {/* Category info row */}
              <div className="flex items-start justify-between mb-1">
                <div className="flex-1 min-w-0">
                  <div className={`font-medium text-surface-900 truncate ${compact ? 'text-[10px]' : 'text-sm'}`}>
                    {item.category}
                  </div>
                  <div className={`text-surface-400 ${compact ? 'text-[8px]' : 'text-xs'}`}>
                    {item.count} transaction{item.count !== 1 ? 's' : ''}
                  </div>
                </div>
                <div className="text-right flex-shrink-0 ml-2">
                  <div
                    className={`font-semibold ${compact ? 'text-[10px]' : 'text-sm'}`}
                    style={{ color: item.color }}
                  >
                    {formatAmount(item.total)}
                  </div>
                  <div className={`text-surface-400 ${compact ? 'text-[8px]' : 'text-xs'}`}>
                    {percentage.toFixed(1)}%
                  </div>
                </div>
              </div>

              {/* Progress bar */}
              <div className={`w-full bg-surface-100 rounded-full overflow-hidden ${compact ? 'h-1' : 'h-1.5'}`}>
                <div
                  className="h-full rounded-full transition-all duration-300"
                  style={{
                    width: `${percentage}%`,
                    backgroundColor: item.color
                  }}
                />
              </div>
            </>
          );

          return isClickable ? (
            <button
              key={item.category || index}
              onClick={() => {
                const catObj = categories.find(c => c.name === item.category && c.type === type);
                onCategoryClick({
                  _id: catObj?._id,
                  name: item.category,
                  type,
                  color: item.color || catObj?.color,
                  icon: catObj?.icon,
                  budget: catObj?.budget,
                  total: item.total,
                  count: item.count
                });
              }}
              className="w-full text-left rounded-lg -mx-1 px-1 py-1 hover:bg-surface-50 active:bg-surface-100 transition-colors"
            >
              {content}
            </button>
          ) : (
            <div key={item.category || index}>
              {content}
            </div>
          );
        })}

        {hasMore && (
          <div className={`text-center text-surface-400 ${compact ? 'text-[8px] pt-1' : 'text-xs pt-2'}`}>
            +{sortedData.length - 4} more categories
          </div>
        )}
      </div>
    </div>
  );
}
